import { useCallback, useState } from "react";
import useIsMounted from "utils/useIsMounted";

export type LocalMutationTuple = [
  () => Promise<void>,
  { loading: boolean; error?: Error; reset: () => void },
];

export default function useLocalMutation(
  action: () => void | Promise<void>,
): LocalMutationTuple {
  const isMounted = useIsMounted();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error>();

  const performMutation = useCallback(async () => {
    try {
      setIsLoading(true);
      await action();
    } catch (e) {
      if (e instanceof Error) {
        if (isMounted.current) setError(e);
      } else {
        throw e;
      }
    } finally {
      if (isMounted.current) setIsLoading(false);
    }
  }, [action, isMounted]);

  const handleReset = useCallback(() => {
    setIsLoading(false);
    setError(undefined);
  }, []);

  return [performMutation, { loading: isLoading, error, reset: handleReset }];
}
